import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _toPropertyKey from "@babel/runtime/helpers/esm/toPropertyKey";
import _extends from "@babel/runtime/helpers/esm/extends";
import { Store } from '@mui/x-internals/store';

// eslint-disable-next-line @typescript-eslint/no-unused-vars

function applyModelInitialValue(controlledValue, defaultValue, fallback) {
  if (controlledValue !== undefined) {
    return controlledValue;
  }
  if (defaultValue !== undefined) {
    return defaultValue;
  }
  return fallback;
}
function normalizeById(items) {
  const ids = [];
  const byId = {};
  const seen = new Set();
  for (const item of items) {
    byId[item.id] = item;
    if (!seen.has(item.id)) {
      seen.add(item.id);
      ids.push(item.id);
    }
  }
  return {
    ids,
    byId
  };
}
function pruneMessageErrorsById(messageErrorsById, messageIds) {
  if (messageIds.length === 0) {
    return {};
  }
  const allowedIds = new Set(messageIds);
  const nextMessageErrorsById = {};
  for (const [messageId, error] of Object.entries(messageErrorsById)) {
    if (allowedIds.has(messageId) && error != null) {
      nextMessageErrorsById[messageId] = error;
    }
  }
  return nextMessageErrorsById;
}

/**
 * Returns `prevIds` when the two arrays contain the same strings in the same
 * order, avoiding a new reference that would trigger downstream re-renders
 * (e.g. `useMessageIds()`) when only message bodies changed.
 */
function stableIds(prevIds, nextIds) {
  if (prevIds === nextIds) {
    return prevIds;
  }
  if (prevIds.length !== nextIds.length) {
    return nextIds;
  }
  for (let i = 0; i < prevIds.length; i += 1) {
    if (prevIds[i] !== nextIds[i]) {
      return nextIds;
    }
  }
  return prevIds;
}
function deriveStateFromParameters(parameters) {
  const {
    ids: messageIds,
    byId: messagesById
  } = normalizeById(applyModelInitialValue(parameters.messages, parameters.initialMessages, []));
  const {
    ids: conversationIds,
    byId: conversationsById
  } = normalizeById(applyModelInitialValue(parameters.conversations, parameters.initialConversations, []));
  return {
    conversationIds,
    conversationsById,
    activeConversationId: parameters.activeConversationIdControlled ? parameters.activeConversationId : applyModelInitialValue(parameters.activeConversationId, parameters.initialActiveConversationId, undefined),
    messageIds,
    messagesById,
    composerValue: applyModelInitialValue(parameters.composerValue, parameters.initialComposerValue, '')
  };
}
export class ChatStore extends Store {
  /** Local (sending) user: explicit prop → members list → active conversation participants. */
  get currentUser() {
    return this.getMemberByRole('user', this.parameters.currentUser);
  }

  /** Assistant member: members list → active conversation participants. */
  get assistantUser() {
    return this.getMemberByRole('assistant');
  }
  getMemberByRole(role, explicit) {
    if (explicit) {
      return explicit;
    }
    if (this.parameters.members) {
      return this.parameters.members.find(m => m.role === role);
    }
    const convId = this.state.activeConversationId;
    const conv = convId ? this.state.conversationsById[convId] : undefined;
    const fromParticipants = conv?.participants?.find(p => p.role === role);
    if (fromParticipants) {
      return fromParticipants;
    }
    // Derive from message authors as last resort
    for (const msg of Object.values(this.state.messagesById)) {
      if (msg.author?.role === role) {
        return msg.author;
      }
    }
    return undefined;
  }
  dirtyControlledModels = new Set();

  /** Whether any controlled model has been internally mutated since the last parameter sync. */
  get hasDirtyControlledModels() {
    return this.dirtyControlledModels.size > 0;
  }
  constructor(parameters = {}) {
    const {
      messageIds,
      messagesById,
      conversationIds,
      conversationsById,
      activeConversationId,
      composerValue
    } = deriveStateFromParameters(parameters);
    super({
      conversationIds,
      conversationsById,
      activeConversationId,
      messageIds,
      messagesById,
      messageErrorsById: {},
      typingByConversation: {},
      activeStreamAbortController: null,
      isStreaming: false,
      hasMoreHistory: false,
      historyCursor: undefined,
      composerValue,
      composerIsComposing: false,
      composerAttachments: [],
      error: null
    });
    this.parameters = parameters;
  }
  updateStateFromParameters = parameters => {
    const newState = {};
    if (parameters.messages !== undefined && (parameters.messages !== this.parameters.messages || this.dirtyControlledModels.has('messages'))) {
      const {
        ids: messageIds,
        byId: messagesById
      } = normalizeById(parameters.messages);
      newState.messageIds = stableIds(this.state.messageIds, messageIds);
      newState.messagesById = messagesById;
      newState.messageErrorsById = pruneMessageErrorsById(this.state.messageErrorsById, messageIds);
      this.dirtyControlledModels.delete('messages');
    }
    if (parameters.conversations !== undefined && (parameters.conversations !== this.parameters.conversations || this.dirtyControlledModels.has('conversations'))) {
      const {
        ids: conversationIds,
        byId: conversationsById
      } = normalizeById(parameters.conversations);
      newState.conversationIds = stableIds(this.state.conversationIds, conversationIds);
      newState.conversationsById = conversationsById;
      this.dirtyControlledModels.delete('conversations');
    }
    if (parameters.activeConversationIdControlled && (parameters.activeConversationId !== this.parameters.activeConversationId || this.dirtyControlledModels.has('activeConversationId'))) {
      newState.activeConversationId = parameters.activeConversationId;
      this.dirtyControlledModels.delete('activeConversationId');
    }
    if (parameters.composerValue !== undefined && (parameters.composerValue !== this.parameters.composerValue || this.dirtyControlledModels.has('composerValue'))) {
      newState.composerValue = parameters.composerValue;
      this.dirtyControlledModels.delete('composerValue');
    }
    this.parameters = parameters;
    this.update(newState);
  };

  /**
   * Returns a cleanup function to be used as a React effect teardown.
   * Called by `useChatInstance` when the store instance changes or the component unmounts.
   */
  disposeEffect = () => {
    return () => {
      this.state.activeStreamAbortController?.abort();
      if (this.state.activeStreamAbortController || this.state.isStreaming) {
        this.update({
          activeStreamAbortController: null,
          isStreaming: false
        });
      }
    };
  };
  registerStoreEffect = (selector, effect) => {
    let previousValue = selector(this.state);
    return this.subscribe(state => {
      const nextValue = selector(state);
      if (!Object.is(previousValue, nextValue)) {
        effect(previousValue, nextValue);
        previousValue = nextValue;
      }
    });
  };
  addMessage = message => {
    const nextMessageIds = this.state.messagesById[message.id] ? this.state.messageIds : [...this.state.messageIds, message.id];
    this.dirtyControlledModels.add('messages');
    this.update({
      messageIds: nextMessageIds,
      messagesById: _extends({}, this.state.messagesById, {
        [message.id]: message
      })
    });
  };
  updateMessage = (id, patch) => {
    const currentMessage = this.state.messagesById[id];
    if (!currentMessage) {
      return;
    }
    this.dirtyControlledModels.add('messages');
    this.update({
      messagesById: _extends({}, this.state.messagesById, {
        [id]: _extends({}, currentMessage, patch)
      })
    });
  };
  removeMessage = id => {
    if (!this.state.messagesById[id]) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    const _this$state$messagesB = this.state.messagesById,
      messagesById = _objectWithoutPropertiesLoose(_this$state$messagesB, [id].map(_toPropertyKey));
    const nextMessageErrorsById = _extends({}, this.state.messageErrorsById);
    delete nextMessageErrorsById[id];
    this.dirtyControlledModels.add('messages');
    this.update({
      messageIds: this.state.messageIds.filter(messageId => messageId !== id),
      messagesById,
      messageErrorsById: nextMessageErrorsById
    });
  };
  prependMessages = messages => {
    if (messages.length === 0) {
      return;
    }
    const nextMessagesById = _extends({}, this.state.messagesById);
    const nextMessageIds = [];
    const existingMessageIds = new Set(this.state.messageIds);
    const prependedMessageIds = new Set();
    for (const message of messages) {
      nextMessagesById[message.id] = message;
      if (!existingMessageIds.has(message.id) && !prependedMessageIds.has(message.id)) {
        prependedMessageIds.add(message.id);
        nextMessageIds.push(message.id);
      }
    }
    this.dirtyControlledModels.add('messages');
    const messageIds = [...nextMessageIds, ...this.state.messageIds];
    this.update({
      messageIds,
      messagesById: nextMessagesById,
      messageErrorsById: pruneMessageErrorsById(this.state.messageErrorsById, messageIds)
    });
  };
  setMessages = messages => {
    const {
      ids: messageIds,
      byId: messagesById
    } = normalizeById(messages);
    this.dirtyControlledModels.add('messages');
    this.update({
      messageIds,
      messagesById,
      messageErrorsById: pruneMessageErrorsById(this.state.messageErrorsById, messageIds)
    });
  };
  setConversations = conversations => {
    const {
      ids: conversationIds,
      byId: conversationsById
    } = normalizeById(conversations);
    this.dirtyControlledModels.add('conversations');
    this.update({
      conversationIds,
      conversationsById
    });
  };
  addConversation = conversation => {
    const nextConversationIds = this.state.conversationsById[conversation.id] ? this.state.conversationIds : [...this.state.conversationIds, conversation.id];
    this.dirtyControlledModels.add('conversations');
    this.update({
      conversationIds: nextConversationIds,
      conversationsById: _extends({}, this.state.conversationsById, {
        [conversation.id]: conversation
      })
    });
  };
  updateConversation = (id, patch) => {
    const currentConversation = this.state.conversationsById[id];
    if (!currentConversation) {
      return;
    }
    this.dirtyControlledModels.add('conversations');
    this.update({
      conversationsById: _extends({}, this.state.conversationsById, {
        [id]: _extends({}, currentConversation, patch)
      })
    });
  };
  removeConversation = id => {
    if (!this.state.conversationsById[id]) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    const _this$state$conversat = this.state.conversationsById,
      conversationsById = _objectWithoutPropertiesLoose(_this$state$conversat, [id].map(_toPropertyKey));
    this.dirtyControlledModels.add('conversations');
    this.update({
      conversationIds: this.state.conversationIds.filter(conversationId => conversationId !== id),
      conversationsById
    });
  };
  setActiveConversation = id => {
    this.dirtyControlledModels.add('activeConversationId');
    this.set('activeConversationId', id);
  };
  setTypingUser = (conversationId, userId, isTyping) => {
    const current = this.state.typingByConversation[conversationId] ?? {};
    if (current[userId] === isTyping) {
      return;
    }
    this.update({
      typingByConversation: _extends({}, this.state.typingByConversation, {
        [conversationId]: _extends({}, current, {
          [userId]: isTyping
        })
      })
    });
  };
  setComposerValue = value => {
    this.dirtyControlledModels.add('composerValue');
    this.set('composerValue', value);
  };
  setComposerIsComposing = value => {
    this.set('composerIsComposing', value);
  };
  setComposerAttachments = attachments => {
    this.set('composerAttachments', attachments);
  };
  addComposerAttachment = attachment => {
    this.setComposerAttachments([...this.state.composerAttachments, attachment]);
  };
  removeComposerAttachment = localId => {
    const nextAttachments = this.state.composerAttachments.filter(attachment => attachment.localId !== localId);
    if (nextAttachments.length === this.state.composerAttachments.length) {
      return;
    }
    this.setComposerAttachments(nextAttachments);
  };
  clearComposer = () => {
    this.dirtyControlledModels.add('composerValue');
    this.update({
      composerValue: '',
      composerIsComposing: false,
      composerAttachments: []
    });
  };
  setStreaming = value => {
    this.set('isStreaming', value);
  };
  setActiveStreamAbortController = value => {
    this.set('activeStreamAbortController', value);
  };
  setError = error => {
    this.set('error', error);
  };
  setMessageError = (messageId, error) => {
    const currentError = this.state.messageErrorsById[messageId];
    if (currentError === error) {
      return;
    }
    const nextMessageErrorsById = _extends({}, this.state.messageErrorsById);
    if (error == null) {
      delete nextMessageErrorsById[messageId];
    } else {
      nextMessageErrorsById[messageId] = error;
    }
    this.set('messageErrorsById', nextMessageErrorsById);
  };
  clearMessageError = messageId => {
    this.setMessageError(messageId, null);
  };
  clearAllMessageErrors = () => {
    if (Object.keys(this.state.messageErrorsById).length === 0) {
      return;
    }
    this.set('messageErrorsById', {});
  };
  setHistoryState = ({
    cursor,
    hasMore
  }) => {
    this.update({
      historyCursor: cursor,
      hasMoreHistory: hasMore
    });
  };
  resetMessages = () => {
    this.dirtyControlledModels.add('messages');
    this.update({
      messageIds: [],
      messagesById: {},
      messageErrorsById: {},
      activeStreamAbortController: null,
      isStreaming: false,
      hasMoreHistory: false,
      historyCursor: undefined,
      error: null
    });
  };
}

/**
 * Narrows the one intentional cursor-erasure boundary used by helpers that
 * operate only on message/conversation state and never touch history cursors.
 */
export function asCursorAgnosticChatStore(store) {
  return store;
}